import { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, Sparkles, Crown, Zap, Loader2, Users, Flame } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATIC_HIGHLIGHTS = {
  free: ['Browse all 25+ templates', 'Live preview editor', 'Download PDFs (with watermark)', 'In-browser PDF tools'],
  pro_monthly: ['Everything in Free', 'No watermark', 'Unlimited AI drafts', 'AI single-field generation', 'Save drafts (coming soon)', 'Cancel any time'],
  pro_annual: ['Everything in Pro Monthly', 'Save 27% vs monthly', 'Priority email support'],
  lifetime: ['Pro features forever', 'One-time payment', 'Founding member perks', 'Future-proof — all new templates included'],
};

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState('');
  const [proof, setProof] = useState(null);
  const { user, login } = useAuth();

  useEffect(() => {
    axios.get(`${API}/plans`).then(r => setPlans(r.data.plans)).catch(() => {});
    axios.get(`${API}/stats/social-proof`).then(r => setProof(r.data)).catch(() => {});
  }, []);

  const startCheckout = async (planId) => {
    if (!user) {
      login('/pricing');
      return;
    }
    setBusy(planId);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start checkout';
      toast.error(typeof msg === 'string' ? msg : 'Could not start checkout');
      setBusy('');
    }
  };

  const findPlan = (id) => plans.find(p => p.id === id);
  const monthly = findPlan('pro_monthly');
  const annual = findPlan('pro_annual');
  const lifetime = findPlan('lifetime');

  const seatsLeft = proof?.lifetime_seats_left ?? null;
  const seatsTotal = proof?.lifetime_seats_total ?? 200;
  const recent = proof?.recent_signups_24h ?? 0;
  // Compute soft urgency: low when <50 seats left
  const lifetimeUrgent = seatsLeft !== null && seatsLeft <= 50;
  const lifetimeSoldOut = seatsLeft === 0;
  const seatPct = seatsLeft !== null ? Math.max(2, Math.min(100, Math.round((seatsLeft / seatsTotal) * 100))) : null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-3">Pricing</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight leading-[0.95]">
          Pay once or monthly.<br />
          <span className="text-orange-600 italic font-serif font-medium">No limits, no watermarks.</span>
        </h1>
        <p className="text-stone-600 mt-5 leading-relaxed">
          Free works for the occasional invoice. Pro is for the people who live in paperwork.
        </p>
      </div>

      {/* Social proof strip */}
      {proof && (
        <div className="flex flex-wrap items-center justify-center gap-3 mb-10" data-testid="social-proof-strip">
          {recent > 0 && (
            <div className="inline-flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-2 text-sm shadow-sm" data-testid="social-proof-recent">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <Users size={13} className="text-stone-500" />
              <span><strong>{recent}</strong> joined in the last 24h</span>
            </div>
          )}
          {!lifetimeSoldOut && (
            <div
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm shadow-sm border ${lifetimeUrgent ? 'bg-orange-600 text-white border-orange-600' : 'bg-white border-stone-200'}`}
              data-testid="social-proof-seats"
            >
              <Flame size={13} className={lifetimeUrgent ? 'text-white' : 'text-orange-600'} />
              <span>
                <strong>{seatsLeft}</strong>
                <span className={lifetimeUrgent ? 'text-white/80' : 'text-stone-500'}> / {seatsTotal} lifetime seats left at $59</span>
              </span>
            </div>
          )}
          {lifetimeSoldOut && (
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm bg-stone-200 text-stone-700" data-testid="social-proof-seats-soldout">
              <Flame size={13} /> Lifetime deal sold out
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* FREE */}
        <PriceCard
          name="Free"
          price="$0"
          interval="forever"
          highlights={STATIC_HIGHLIGHTS.free}
          cta={user ? 'Current plan' : 'Get started'}
          disabled
          testId="plan-free"
        />

        {/* PRO MONTHLY */}
        <PriceCard
          name={monthly?.name || 'Pro'}
          price={monthly ? `$${monthly.amount.toFixed(0)}` : '$9'}
          interval="per month"
          highlights={STATIC_HIGHLIGHTS.pro_monthly}
          cta={busy === 'pro_monthly' ? 'Loading…' : 'Start Pro'}
          icon={<Sparkles size={14} />}
          onClick={() => startCheckout('pro_monthly')}
          busy={busy === 'pro_monthly'}
          testId="plan-pro-monthly"
        />

        {/* PRO ANNUAL — highlighted */}
        <PriceCard
          name="Pro Annual"
          price={annual ? `$${annual.amount.toFixed(0)}` : '$79'}
          interval="per year"
          highlights={STATIC_HIGHLIGHTS.pro_annual}
          cta={busy === 'pro_annual' ? 'Loading…' : 'Save 27%'}
          icon={<Zap size={14} />}
          onClick={() => startCheckout('pro_annual')}
          busy={busy === 'pro_annual'}
          highlighted
          badge="Most popular"
          testId="plan-pro-annual"
        />

        {/* LIFETIME */}
        <PriceCard
          name="Lifetime"
          price={lifetime ? `$${lifetime.amount.toFixed(0)}` : '$59'}
          interval="one-time"
          highlights={STATIC_HIGHLIGHTS.lifetime}
          cta={lifetimeSoldOut ? 'Sold out' : busy === 'lifetime' ? 'Loading…' : 'Buy Lifetime'}
          icon={<Crown size={14} />}
          onClick={() => startCheckout('lifetime')}
          busy={busy === 'lifetime'}
          disabled={lifetimeSoldOut}
          dark
          badge={lifetimeUrgent && !lifetimeSoldOut ? `Only ${seatsLeft} left` : 'Launch deal'}
          testId="plan-lifetime"
          footer={seatPct !== null && !lifetimeSoldOut ? (
            <div className="mt-4">
              <div className="flex justify-between text-[10px] uppercase tracking-wider text-stone-400 mb-1.5">
                <span>{seatsLeft} of {seatsTotal} left</span>
                <span>{Math.round(100 - (seatsLeft / seatsTotal) * 100)}% claimed</span>
              </div>
              <div className="h-1.5 bg-stone-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all"
                  style={{ width: `${100 - seatPct}%` }}
                />
              </div>
            </div>
          ) : null}
        />
      </div>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-stone-600 max-w-5xl mx-auto">
        <Faq q="Can I cancel anytime?" a="Yes. Pro Monthly is billed every 31 days — cancel and you keep access until the period ends." />
        <Faq q="Is my data private?" a="All PDFs are generated in your browser. We never upload your filled documents to our servers." />
        <Faq q="What payment methods?" a="All major cards via Stripe. Lifetime is a one-time charge." />
      </div>
    </div>
  );
}

function PriceCard({ name, price, interval, highlights, cta, onClick, disabled, busy, highlighted, dark, badge, icon, testId, footer }) {
  const base = dark
    ? 'bg-stone-900 text-white border-stone-900'
    : highlighted
      ? 'bg-white border-orange-500 ring-2 ring-orange-500 shadow-lg'
      : 'bg-white border-stone-200';
  const ctaCls = dark
    ? 'bg-orange-600 hover:bg-orange-700 text-white'
    : highlighted
      ? 'bg-orange-600 hover:bg-orange-700 text-white'
      : 'bg-stone-900 hover:bg-stone-800 text-white';

  return (
    <div className={`relative rounded-3xl border p-6 flex flex-col ${base}`} data-testid={testId}>
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-bold">
          {badge}
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        {icon && <span className={dark ? 'text-orange-400' : 'text-orange-600'}>{icon}</span>}
        <span className={`text-xs uppercase tracking-wider ${dark ? 'text-stone-400' : 'text-stone-500'}`}>{name}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display font-black text-5xl tracking-tight">{price}</span>
        <span className={`text-sm ${dark ? 'text-stone-400' : 'text-stone-500'}`}>{interval}</span>
      </div>
      <ul className={`mt-6 space-y-2.5 text-sm flex-1 ${dark ? 'text-stone-300' : 'text-stone-700'}`}>
        {highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check size={14} className={`shrink-0 mt-0.5 ${dark ? 'text-orange-400' : 'text-orange-600'}`} />
            <span>{h}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        disabled={disabled || busy}
        className={`mt-7 w-full py-3 rounded-full text-sm font-medium btn-clay disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 ${ctaCls}`}
        data-testid={`${testId}-cta`}
      >
        {busy && <Loader2 size={14} className="animate-spin" />}
        {cta}
      </button>
      {footer}
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-stone-200">
      <div className="font-display font-bold text-stone-900 mb-1.5">{q}</div>
      <div className="text-stone-600 leading-relaxed">{a}</div>
    </div>
  );
}
